'use client';

import Image from 'next/image';
import Link from 'next/link';

import type { IconsWithTextType } from '@eliadmin/core/src/api/general/types/general-types';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { IoMdStar } from 'react-icons/io';
import { IoStorefront } from 'react-icons/io5';
import { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

export type HeroProps = {
  title?: string;
  subtitle?: string;
  description?: string;
  longDescription?: string;
  image?: string;
  smallIcons: TDynamicData[];
  largeIcons: TDynamicData[];
};

export function Hero({
  title,
  subtitle,
  description,
  longDescription,
  image,
  smallIcons,
  largeIcons,
}: HeroProps) {
  const { t } = useTranslations();

  return (
    <section
      className={cn(
        'custom-section',
        'relative z-10 gap-12 bg-[#FCFBFF] pb-10 pt-10 sm:gap-16 sm:pb-0 sm:pt-[50px] lg:gap-[100px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-12 md:flex-row md:items-stretch md:justify-between',
        )}
      >
        <div className="flex w-full flex-col items-start justify-between gap-4 sm:gap-6 md:max-w-[588px]">
          <div className="flex h-[34px] w-fit items-center justify-start gap-2 rounded-[8px] border px-4 sm:h-9">
            <IoStorefront size={20} className="text-[#117F11]" />
            <Paragraph className="font-semibold" size="sm" color="foreground">
              {t('text3872')}
            </Paragraph>
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-2 sm:gap-3">
            <h1
              className={cn('h1', 'text-left font-semibold tracking-tighter text-primary')}
              dangerouslySetInnerHTML={{ __html: title ?? '' }}
            />
            <Heading title={subtitle ?? ''} />
          </div>
          <Paragraph innerHtml={description} size="base" color="foreground" className="w-full" />
          {/* <div className="flex w-full items-center justify-start gap-2 sm:gap-3">
            <Button asChild className="w-full whitespace-nowrap sm:w-fit">
              <Link prefetch={false} href={'#'}>
                {t('text3873')}
              </Link>
            </Button>
            <Button
              asChild
              variant={'primary-foreground'}
              className="w-full whitespace-nowrap sm:w-fit"
            >
              <Link prefetch={false} href={'#'}>
                <ButtonIcon alt={t('text3874')} url="/images/icons/roadmap.svg" />
                {t('text3874')}
              </Link>
            </Button>
          </div> */}
          <Paragraph
            innerHtml={longDescription}
            color="foreground"
            size="base"
            className="hidden w-full border-l-4 pl-6 sm:block"
          />
        </div>
        <div className="relative h-[441px] w-full sm:h-[600px] sm:max-w-[486px]">
          <div
            className={cn(
              'absolute left-6 top-6 z-10 flex items-center justify-start gap-3 rounded-[12px] bg-white p-3 sm:p-4 md:-left-6',
              'review-card-statistic',
            )}
          >
            <Heading title="4,8" tag="div" visualTag="h2" />
            <div className="flex flex-col items-start justify-start gap-1">
              <div className="flex items-center justify-start gap-[1px]">
                {[...Array(5)].map((_, index) => (
                  <IoMdStar key={index} size={20} className="text-[#F5B42F]" />
                ))}
              </div>
              <Paragraph size="sm" color="foreground">
                1 350+ {t('text2360')}
              </Paragraph>
            </div>
          </div>
          <Image
            src={image || '/images/placeholder.png'}
            alt=""
            fill
            fetchPriority="high"
            loading="eager"
            sizes="(max-width: 639px) calc(100vw - 32px), 486px"
            className="absolute inset-0 z-0 h-full w-full overflow-hidden rounded-r-full object-cover object-center"
          />
          <div className="absolute bottom-6 right-0 z-10 flex flex-col gap-[6px]">
            {smallIcons.map(icon => (
              <div
                key={icon.name}
                className="flex h-10 w-full items-center justify-start gap-[6px] rounded-[16px] bg-primary px-2 sm:h-12"
              >
                <ButtonIcon
                  alt={icon.name}
                  url={icon.image?.url ?? '/images/placeholder.png'}
                  className="max-h-6 min-h-6 min-w-6 max-w-6"
                />
                <Paragraph size="sm" color="white" className="whitespace-nowrap font-semibold">
                  {icon.name}
                </Paragraph>
              </div>
            ))}
          </div>
        </div>
        <Paragraph
          innerHtml={longDescription}
          color="foreground"
          size="base"
          className="block w-full border-l-4 pl-6 sm:hidden"
        />
      </div>
      <div className={cn('custom-container', 'grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6')}>
        {largeIcons.map(icon => (
          <IconCard icon={icon} key={icon.name} />
        ))}
      </div>
    </section>
  );
}

function IconCard({ icon }: { icon: TDynamicData }) {
  return (
    <div
      className={cn(
        'relative z-10 col-span-1 flex w-full items-center justify-start gap-3 rounded-[12px] bg-white p-3 sm:gap-4 sm:p-4 lg:gap-6 lg:p-6',
        'ea-icon-card',
      )}
    >
      <div className="absolute right-3 top-3 h-2 w-2 rounded-full bg-primary sm:right-4 sm:top-4 lg:right-6 lg:top-6" />
      <div className="flex max-h-12 min-h-12 min-w-12 max-w-12 items-center justify-center rounded-[12px] border md:max-h-[76px] md:min-h-[76px] md:min-w-[76px] md:max-w-[76px]">
        <ButtonIcon
          alt={icon.name}
          url={icon?.image?.url ?? '/images/placeholder.png'}
          className="md:max-h-8 md:min-h-8 md:min-w-8 md:max-w-8"
        />
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-[6px] md:gap-2">
        <Heading title={icon.name} tag="h3" visualTag="h5" />
        <Paragraph
          innerHtml={icon.fields[1]?.value}
          color="foreground"
          className="w-full"
          size="base"
        />
      </div>
    </div>
  );
}
